import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Divider } from 'primereact/divider';
import { Dialog } from 'primereact/dialog';
import { InputOtp } from 'primereact/inputotp';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../../config/axios.config';
import EquifaxOutputComponent from '../output/EquifaxOutputComponent';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';
import { validateStatePincode } from '../../../utils/pincodeStateMapping';

const PAN_REGEX = /^[A-Z]{5}\d{4}[A-Z]$/;
const MOBILE_REGEX = /^[6-9]\d{9}$/;
const PINCODE_REGEX = /^[1-9]\d{5}$/;
const NAME_REGEX = /^[A-Za-z\s]+$/;

const STATES = [
    { label: 'Andaman and Nicobar Islands', value: 'AN' },
    { label: 'Andhra Pradesh', value: 'AP' },
    { label: 'Arunachal Pradesh', value: 'AR' },
    { label: 'Assam', value: 'AS' },
    { label: 'Bihar', value: 'BR' },
    { label: 'Chandigarh', value: 'CH' },
    { label: 'Chhattisgarh', value: 'CT' },
    { label: 'Dadra and Nagar Haveli', value: 'DN' },
    { label: 'Daman and Diu', value: 'DD' },
    { label: 'Delhi', value: 'DL' },
    { label: 'Goa', value: 'GA' },
    { label: 'Gujarat', value: 'GJ' },
    { label: 'Haryana', value: 'HR' },
    { label: 'Himachal Pradesh', value: 'HP' },
    { label: 'Jammu and Kashmir', value: 'JK' },
    { label: 'Jharkhand', value: 'JH' },
    { label: 'Karnataka', value: 'KA' },
    { label: 'Kerala', value: 'KL' },
    { label: 'Ladakh', value: 'LA' },
    { label: 'Lakshadweep', value: 'LD' },
    { label: 'Madhya Pradesh', value: 'MP' },
    { label: 'Maharashtra', value: 'MH' },
    { label: 'Manipur', value: 'MN' },
    { label: 'Meghalaya', value: 'ML' },
    { label: 'Mizoram', value: 'MZ' },
    { label: 'Nagaland', value: 'NL' },
    { label: 'Odisha', value: 'OR' },
    { label: 'Puducherry', value: 'PY' },
    { label: 'Punjab', value: 'PB' },
    { label: 'Rajasthan', value: 'RJ' },
    { label: 'Sikkim', value: 'SK' },
    { label: 'Tamil Nadu', value: 'TN' },
    { label: 'Telangana', value: 'TG' },
    { label: 'Tripura', value: 'TR' },
    { label: 'Uttar Pradesh', value: 'UP' },
    { label: 'Uttarakhand', value: 'UT' },
    { label: 'West Bengal', value: 'WB' },
];

const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};


const EquifaxInputComponent = ({
    // When the component is mounted as a stand-alone page (/reports/equifax/:id),
    // these props are undefined and we fall back to URL params + self-fetch
    // (legacy behaviour). When mounted from inside the Lead Wizard (via
    // VerificationSidebar → DynamicReportComponent), the host passes them so
    // the form opens pre-filled with whatever the DSA already typed in the
    // wizard's main fields (PAN, name, phone, DOB, address, pincode).
    serviceId: serviceIdProp,
    serviceDetails: serviceDetailsProp,
    leadId,
    initialValues,
    // When set, the component opens directly in result-mode showing the
    // existing report (instead of the empty input form). Used by the wizard
    // when the DSA closes the sidebar after fetching and reopens it — we
    // remember the report on the wizard side and re-feed it here so they
    // don't have to re-enter PAN/name/etc.
    initialReportData,
} = {}) => {
    const routeParams = useParams();
    const serviceId = serviceIdProp ?? routeParams.serviceId;
    const toast = useRef(null);
    const submittingRef = useRef(false);

    // Seed the form from initialValues (host-provided) so the wizard's
    // existing PAN / name / phone / dob / address / pincode flow through
    // without re-typing. Falls back to empty defaults when absent so the
    // stand-alone page behaviour is unchanged.
    const seedDate = (v) => {
        if (!v) return null;
        if (v instanceof Date) return v;
        const d = new Date(v);
        return isNaN(d.getTime()) ? null : d;
    };
    const [formData, setFormData] = useState({
        full_name:     initialValues?.full_name     || '',
        phone:         initialValues?.phone         || '',
        date_of_birth: seedDate(initialValues?.date_of_birth || initialValues?.dob),
        pan:           (initialValues?.pan          || '').toString().toUpperCase(),
        state:         initialValues?.state         || '',
        pincode:       initialValues?.pincode       || '',
        address:       initialValues?.address       || '',
        consent: false,
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    // ── OTP consent gate ──────────────────────────────────────────────
    // Before we spend credits on the Equifax call, the borrower must prove
    // consent by verifying an OTP sent to the Mobile Number on this form.
    // Flow: Generate → confirm dialog → send OTP → this dialog → verify →
    // then submit() hits /get-equifax-report. Free OTP (no credits) via
    // /api/consent-otp/send + /verify (MSG91-backed).
    const [otpDialogVisible, setOtpDialogVisible] = useState(false);
    const [otpValue, setOtpValue] = useState('');
    const [otpSending, setOtpSending] = useState(false);   // send / resend in flight
    const [otpVerifying, setOtpVerifying] = useState(false); // verify in flight
    const [otpError, setOtpError] = useState('');
    const [resendCooldown, setResendCooldown] = useState(0); // seconds until resend allowed
    const [otpLocked, setOtpLocked] = useState(false);       // backend 429 lockout — no more sends/verifies

    // Resend cooldown ticker — counts down to 0 after each send.
    useEffect(() => {
        if (resendCooldown <= 0) return;
        const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [resendCooldown]);
    // Seed with initialReportData so reopening the sidebar after a fetch
    // lands directly in result-mode (CIBIL hero + active loans + EMI +
    // PDF download), not the empty input form.
    const [reportData, setReportData] = useState(initialReportData ?? null);
    // Host-supplied serviceDetails (when mounted inside VerificationSidebar)
    // wins; otherwise fetch on mount as before.
    const [serviceDetails, setServiceDetails] = useState(serviceDetailsProp ?? null);
    const [fetchingService, setFetchingService] = useState(!serviceDetailsProp);

    useEffect(() => {
        // Skip the self-fetch when the host already provided serviceDetails.
        if (serviceDetailsProp) return;
        if (!serviceId) { setFetchingService(false); return; }
        const load = async () => {
            setFetchingService(true);
            try {
                const res = await axiosInstance.get(`/user/report-services/${serviceId}`);
                if (res.data.success && res.data.data) setServiceDetails(res.data.data);
            } catch (error) {
                console.error('Load service details failed:', error?.message || 'Unknown error');
                setServiceDetails({ available_credits: 'N/A', credits_required: 'N/A' });
            } finally {
                setFetchingService(false);
            }
        };
        load();
    }, [serviceId, serviceDetailsProp]);

    const handleField = (field, transform = (v) => v) => (e) => {
        const raw = e.target ? e.target.value : e.value;
        setFormData((prev) => ({ ...prev, [field]: transform(raw ?? '') }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    };

    const handleConsentChange = (e) => {
        setFormData((prev) => ({ ...prev, consent: e.checked }));
        if (errors.consent) setErrors((prev) => ({ ...prev, consent: '' }));
    };

    const handlePincodeBlur = () => {
        const { pincode, state } = formData;
        if (pincode.length === 6 && state) {
            const validation = validateStatePincode(pincode, state);
            if (!validation.isValid) setErrors((prev) => ({ ...prev, pincode: validation.message }));
        }
    };

    const validate = () => {
        const next = {};
        if (!formData.pan) next.pan = 'PAN number is required';
        else if (!PAN_REGEX.test(formData.pan)) next.pan = 'Invalid PAN format (e.g., ABCDE1234F)';

        if (!formData.full_name.trim()) next.full_name = 'Full name is required';
        else if (formData.full_name.trim().length < 3) next.full_name = 'Full name must be at least 3 characters';
        else if (!NAME_REGEX.test(formData.full_name)) next.full_name = 'Full name should contain only letters and spaces';

        if (!formData.phone) next.phone = 'Mobile number is required';
        else if (!MOBILE_REGEX.test(formData.phone)) next.phone = 'Enter a valid 10-digit Indian mobile (must start with 6–9)';

        if (!formData.date_of_birth) next.date_of_birth = 'Date of birth is required';
        else {
            const today = new Date();
            const dob = new Date(formData.date_of_birth);
            let age = today.getFullYear() - dob.getFullYear();
            const m = today.getMonth() - dob.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age -= 1;
            if (age < 18) next.date_of_birth = 'Must be 18 years or older';
            else if (age > 100) next.date_of_birth = 'Please enter a valid date of birth';
        }

        if (!formData.state) next.state = 'Please select a state';

        if (!formData.pincode) next.pincode = 'Pincode is required';
        else if (!PINCODE_REGEX.test(formData.pincode)) next.pincode = 'Invalid pincode (6 digits, cannot start with 0)';
        else if (formData.state) {
            const validation = validateStatePincode(formData.pincode, formData.state);
            if (!validation.isValid) next.pincode = validation.message;
        }

        if (!formData.address.trim()) next.address = 'Address is required';
        else if (formData.address.trim().length < 10) next.address = 'Please enter a complete address (min 10 characters)';

        if (!formData.consent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const submit = async (consentToken) => {
        setLoading(true);
        try {
            const payload = {
                full_name: formData.full_name.trim(),
                phone: formData.phone,
                date_of_birth: formatDate(formData.date_of_birth),
                pan: formData.pan,
                state: formData.state,
                pincode: formData.pincode,
                address: formData.address.trim(),
                consent: 'Y',
                // Server-side OTP consent proof — the backend rejects the call
                // (403) without a valid single-use token. See verifyConsentOtp.
                consent_token: consentToken,
                api_provider_service_id: serviceId,
                application_id: 'bureaubox',
                // Forward leadId so the saved BureauBoxReport row binds to
                // the lead via the model's creating-hook. Omitted when the
                // component is used stand-alone (no lead context).
                ...(leadId ? { lead_id: parseInt(leadId, 10) } : {}),
            };
            const res = await axiosInstance.post('/get-equifax-report', payload);
            if (!res.data.success) throw new Error(res.data.message || 'Report generation failed');
            setReportData({
                ...res.data,
                pdf_password_hint: res.data.pdf_password_hint || res.data.data?.pdf_password_hint,
            });
            toast.current?.show({ severity: 'success', summary: 'Credit report ready', detail: 'Report rendered below', life: 2000 });

            // Notify any host (the Lead Wizard's useEquifax hook) that the
            // report is ready so it can flow extracted fields into formData.
            // Matches the SimpleReportForm dispatch shape — listeners filter
            // by serviceId so other components' listeners are no-ops here.
            //
            // `userInput` carries what the DSA typed into THIS form so the
            // wizard's just-in-time createLead has reliable identity (name,
            // phone, PAN) even when extracted_data is sparse. Without this,
            // createLead reads formData (still empty when Equifax is the
            // first action) and bails out with "name/phone/PAN required".
            try {
                window.dispatchEvent(new CustomEvent('verification-report-saved', {
                    detail: {
                        serviceId: parseInt(serviceId, 10),
                        data: res.data?.data ?? null,
                        extracted_data: res.data?.data?.extracted_data ?? res.data?.extracted_data ?? null,
                        response: res.data,
                        userInput: {
                            full_name: formData.full_name?.trim() || null,
                            phone: formData.phone || null,
                            pan: formData.pan || null,
                            date_of_birth: formData.date_of_birth || null,
                            state: formData.state || null,
                            pincode: formData.pincode || null,
                            address: formData.address?.trim() || null,
                        },
                    },
                }));
            } catch (e) {
                // Custom events not supported — degraded fallback is the
                // VerificationSidebar's manual /borrower-reports/latest poll.
            }
            try {
                const refreshed = await axiosInstance.get(`/user/report-services/${serviceId}`);
                if (refreshed.data.success && refreshed.data.data) setServiceDetails(refreshed.data.data);
            } catch (refreshErr) {
                console.error('Credit refresh failed:', refreshErr?.message || 'Unknown error');
            }
        } catch (error) {
            console.error('Equifax report failed:', error?.message || 'Unknown error');
            const status = error.response?.status;
            const map = {
                500: 'Internal server error. Please try again later.',
                422: 'Please check your input details and try again.',
                429: 'Too many requests. Please wait a minute before trying again.',
                400: 'Invalid phone or PAN details. Please verify your information.',
                404: 'No credit record found for these details.',
            };
            let detail = map[status] || error.response?.data?.message || error.message || 'Report generation failed';
            if (status === 402) {
                const c = error.response?.data || {};
                detail = `Insufficient credits. Required: ${c.required_credits ?? 'N/A'}, Available: ${c.current_balance ?? 'N/A'}`;
            }
            toast.current?.show({ severity: 'error', summary: 'Report failed', detail, life: 3500 });
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    // Send (or resend) the consent OTP to the mobile number on the form, then
    // open the OTP entry dialog. Called when the DSA confirms the credit-spend
    // dialog. Does NOT deduct credits.
    const sendConsentOtp = async ({ isResend = false } = {}) => {
        setOtpSending(true);
        setOtpError('');
        try {
            const res = await axiosInstance.post('/consent-otp/send', { mobile: formData.phone });
            if (!res.data.success) throw new Error(res.data.message || 'Failed to send OTP');
            setOtpValue('');
            setResendCooldown(30);
            if (!isResend) setOtpDialogVisible(true);
            toast.current?.show({
                severity: 'success',
                summary: isResend ? 'OTP resent' : 'OTP sent',
                detail: `A verification code was sent to ${formData.phone}`,
                life: 2500,
            });
        } catch (error) {
            const detail = error.response?.data?.message || error.message || 'Failed to send OTP';
            // 429 = backend rate-limit lockout. Mark locked so the resend
            // button stays disabled and, if the dialog is open, the message
            // is shown inline; the DSA must wait out the window.
            if (error.response?.status === 429) {
                setOtpLocked(true);
                setResendCooldown(0);
                if (isResend) setOtpError(detail);
            }
            // If the very first send fails, release the submit lock so the DSA
            // can retry from the form. On resend we keep the dialog open.
            if (!isResend) submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'OTP send failed', detail, life: 4500 });
        } finally {
            setOtpSending(false);
        }
    };

    // Verify the typed OTP. On success, close the dialog and proceed to the
    // actual (billable) Equifax call via submit().
    const verifyConsentOtp = async () => {
        if (!/^\d{4,8}$/.test(otpValue)) {
            setOtpError('Enter the complete OTP');
            return;
        }
        setOtpVerifying(true);
        setOtpError('');
        try {
            const res = await axiosInstance.post('/consent-otp/verify', {
                mobile: formData.phone,
                otp: otpValue,
            });
            if (!res.data.success) throw new Error(res.data.message || 'OTP verification failed');
            const consentToken = res.data.consent_token;
            if (!consentToken) throw new Error('Consent token missing from server response');
            setOtpDialogVisible(false);
            setOtpValue('');
            toast.current?.show({ severity: 'success', summary: 'Verified', detail: 'Consent confirmed — generating report', life: 2000 });
            // Now hit the real bureau API, forwarding the single-use consent
            // token so the backend allows the (billable) call.
            await submit(consentToken);
        } catch (error) {
            const detail = error.response?.data?.message || error.message || 'OTP verification failed';
            setOtpError(detail);
            // 429 = too many wrong attempts. The current OTP is dead; require
            // a fresh one. Only a new send (which the backend must allow under
            // the send limit) can proceed.
            if (error.response?.status === 429) setOtpLocked(true);
        } finally {
            setOtpVerifying(false);
        }
    };

    // DSA cancels the OTP step — abort the whole flow, no credits spent.
    const cancelOtp = () => {
        setOtpDialogVisible(false);
        setOtpValue('');
        setOtpError('');
        setOtpLocked(false);
        submittingRef.current = false;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const next = validate();
        if (Object.keys(next).length > 0) {
            setErrors(next);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix the highlighted fields', life: 3000 });
            return;
        }
        setErrors({});
        submittingRef.current = true;
        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }
        confirmDialog({
            // Group-scope this dialog so when EquifaxInputComponent is mounted
            // INSIDE the Lead Wizard (which has its own un-grouped <ConfirmDialog/>)
            // the confirmDialog() call only triggers the local instance below
            // — preventing the dialog from rendering twice (once locally and
            // once via the wizard's global instance).
            group: 'equifax-input',
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">Name:</span> <span className="text-900 font-medium">{formData.full_name}</span></div>
                    <div className="mb-2"><span className="text-600">PAN:</span> <span className="text-900 font-medium">{formData.pan}</span></div>
                    <div className="mb-2"><span className="text-600">Mobile:</span> <span className="text-900 font-medium">{formData.phone}</span></div>
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits to deduct:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: 'Confirm credit report generation',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Send OTP & continue',
            rejectLabel: 'Cancel',
            // Instead of calling the billable API directly, first send a
            // consent OTP to the borrower's mobile. submit() runs only after
            // the OTP is verified (see verifyConsentOtp).
            accept: () => sendConsentOtp(),
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '500px' },
            draggable: false,
        });
    };

    const handleDownloadPDF = async () => {
        const reportId = reportData?.report_id || reportData?.id || reportData?.data?.report_id || reportData?.data?.id;
        if (!reportId) {
            toast.current?.show({ severity: 'warn', summary: 'PDF unavailable', detail: 'Report ID not available', life: 3000 });
            return;
        }
        try {
            // Backend streams the password-protected PDF as a binary attachment.
            // We pull it as a blob, build an object URL, and trigger a download
            // with an off-DOM anchor — works under tenancy where the static
            // `/storage/...` URL doesn't resolve (no symlink, tenant-scoped path).
            const res = await axiosInstance.post(
                `/gridlines/equifax-credit-report/generate-pdf/${reportId}`,
                {},
                { responseType: 'blob' }
            );

            // Try to read the filename from Content-Disposition; fall back to a
            // sensible default. Backend sets `attachment; filename="..."`.
            let filename = `equifax-report-${reportId}.pdf`;
            const disposition = res.headers?.['content-disposition'] || '';
            const match = /filename="?([^"]+)"?/i.exec(disposition);
            if (match?.[1]) filename = match[1];

            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.current?.show({ severity: 'success', summary: 'Download started', detail: filename, life: 2500 });
        } catch (error) {
            // Error responses are JSON, but axios returned a blob — read it to
            // extract the `message` for the toast. Falls back to the generic
            // status-based message if parsing fails.
            let serverMessage = null;
            if (error.response?.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    serverMessage = JSON.parse(text)?.message;
                } catch (_) { /* ignore — fall through */ }
            }

            console.error('Equifax PDF download failed:', error?.message || 'Unknown error');
            const status = error.response?.status;
            const detail =
                serverMessage ||
                (status === 404 ? 'Report not found. Please regenerate the report first.' :
                status === 400 ? 'No data available for PDF generation.' :
                status === 500 ? 'Internal server error. Please try again later.' :
                'PDF download failed');
            toast.current?.show({ severity: 'error', summary: 'Download failed', detail, life: 3500 });
        }
    };

    const handleAnother = () => {
        setReportData(null);
        setFormData({ full_name: '', phone: '', date_of_birth: null, pan: '', state: '', pincode: '', address: '', consent: false });
        setErrors({});
    };

    const fieldClass = (f) => classNames('w-full p-inputtext-sm', { 'p-invalid': !!errors[f] });
    const fieldClassMono = (f) => classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!errors[f] });
    const errTip = (f, helpId) => errors[f] && (
        <small id={helpId} className="p-error block mt-1" role="alert">
            <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />{errors[f]}
        </small>
    );

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog group="equifax-input" />

            {/* Consent OTP gate — shown after the DSA confirms the credit spend.
                Borrower verifies the OTP sent to their mobile before we call
                the (billable) Equifax API. */}
            <Dialog
                header="Verify borrower's mobile"
                visible={otpDialogVisible}
                onHide={cancelOtp}
                modal
                draggable={false}
                dismissableMask={false}
                closable={!otpVerifying}
                style={{ width: '90vw', maxWidth: '440px' }}
            >
                <div className="flex flex-column align-items-center text-center">
                    <i className="pi pi-shield text-primary" style={{ fontSize: '2.5rem' }} aria-hidden="true" />
                    <p className="text-sm text-700 mt-3 mb-1">
                        Enter the OTP sent to
                    </p>
                    <p className="text-base font-semibold text-900 m-0 mb-4">
                        <i className="pi pi-phone text-500 mr-1" aria-hidden="true" />{formData.phone}
                    </p>

                    <InputOtp
                        value={otpValue}
                        onChange={(e) => { setOtpValue((e.value ?? '').toString()); if (otpError) setOtpError(''); }}
                        length={6}
                        integerOnly
                        disabled={otpVerifying || otpLocked}
                    />

                    {otpError && (
                        <small className="p-error block mt-3" role="alert">
                            <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />{otpError}
                        </small>
                    )}

                    <div className="flex align-items-center gap-2 mt-4 w-full justify-content-center">
                        <Button
                            label="Verify & Generate"
                            icon="pi pi-check"
                            onClick={verifyConsentOtp}
                            loading={otpVerifying}
                            disabled={otpVerifying || otpLocked || otpValue.length < 6}
                            size="small"
                        />
                    </div>

                    <div className="mt-3">
                        {otpLocked ? (
                            <span className="text-xs text-red-500">
                                <i className="pi pi-lock mr-1" aria-hidden="true" />OTP limit reached — please try again later.
                            </span>
                        ) : resendCooldown > 0 ? (
                            <span className="text-xs text-500">
                                Resend OTP in {resendCooldown}s
                            </span>
                        ) : (
                            <Button
                                label="Resend OTP"
                                icon="pi pi-replay"
                                link
                                size="small"
                                loading={otpSending}
                                disabled={otpSending || otpVerifying}
                                onClick={() => sendConsentOtp({ isResend: true })}
                                className="p-0 text-xs"
                            />
                        )}
                    </div>
                </div>
            </Dialog>

            <ReportBanner
                icon="pi-chart-line"
                title="Equifax Credit Report"
                subtitle="Pull a comprehensive Equifax credit report — bureau score, account summary, payment history"
                availableCredits={serviceDetails?.available_credits}
                requiredCredits={serviceDetails?.credits_required}
                loading={fetchingService}
            />

            <Card>
                {!reportData ? (
                    <form onSubmit={handleSubmit} noValidate>
                        {/* Borrower identity */}
                        <div className="flex align-items-center gap-2 mb-3">
                            <i className="pi pi-user text-primary" aria-hidden="true" />
                            <span className="text-sm font-semibold text-900 uppercase">Borrower Identity</span>
                        </div>
                        <div className="grid">
                            <div className="field col-12 md:col-6">
                                <label htmlFor="pan" className="block font-medium text-sm mb-2">
                                    PAN Number <span className="text-red-500">*</span>
                                </label>
                                <IconField iconPosition="left" className="w-full">
                                    <InputIcon className="pi pi-id-card" />
                                    <InputText id="pan" value={formData.pan} onChange={handleField('pan', (v) => v.toUpperCase().trim())}
                                        placeholder="ABCDE1234F" maxLength={10} autoCapitalize="characters" spellCheck={false}
                                        disabled={loading} className={fieldClassMono('pan')} aria-invalid={!!errors.pan} aria-describedby="pan-help" />
                                </IconField>
                                {errTip('pan', 'pan-help')}
                            </div>
                            <div className="field col-12 md:col-6">
                                <label htmlFor="full_name" className="block font-medium text-sm mb-2">
                                    Full Name <span className="text-red-500">*</span>
                                </label>
                                <IconField iconPosition="left" className="w-full">
                                    <InputIcon className="pi pi-user" />
                                    <InputText id="full_name" value={formData.full_name} onChange={handleField('full_name')}
                                        placeholder="As per PAN" maxLength={100} autoCapitalize="words"
                                        disabled={loading} className={fieldClass('full_name')} aria-invalid={!!errors.full_name} aria-describedby="full_name-help" />
                                </IconField>
                                {errTip('full_name', 'full_name-help')}
                            </div>
                            <div className="field col-12 md:col-6">
                                <label htmlFor="phone" className="block font-medium text-sm mb-2">
                                    Mobile Number <span className="text-red-500">*</span>
                                </label>
                                <IconField iconPosition="left" className="w-full">
                                    <InputIcon className="pi pi-phone" />
                                    <InputText id="phone" value={formData.phone} onChange={handleField('phone', (v) => v.replace(/\D/g, '').slice(0, 10))}
                                        placeholder="9876543210" maxLength={10} inputMode="tel" autoComplete="tel"
                                        disabled={loading} className={fieldClassMono('phone')} aria-invalid={!!errors.phone} aria-describedby="phone-help" />
                                </IconField>
                                {errTip('phone', 'phone-help')}
                            </div>
                            <div className="field col-12 md:col-6">
                                <label htmlFor="dob" className="block font-medium text-sm mb-2">
                                    Date of Birth <span className="text-red-500">*</span>
                                </label>
                                <Calendar id="dob" value={formData.date_of_birth} onChange={handleField('date_of_birth')}
                                    dateFormat="dd/mm/yy" placeholder="DD/MM/YYYY" maxDate={new Date()}
                                    yearNavigator yearRange={`1925:${new Date().getFullYear()}`} monthNavigator showIcon
                                    disabled={loading} className={fieldClass('date_of_birth')} inputClassName="p-inputtext-sm"
                                    aria-invalid={!!errors.date_of_birth} aria-describedby="dob-help" />
                                {errTip('date_of_birth', 'dob-help')}
                            </div>
                        </div>

                        {/* Address */}
                        <Divider />
                        <div className="flex align-items-center gap-2 mb-3">
                            <i className="pi pi-map-marker text-primary" aria-hidden="true" />
                            <span className="text-sm font-semibold text-900 uppercase">Address</span>
                        </div>
                        <div className="grid">
                            <div className="field col-12 md:col-4">
                                <label htmlFor="state" className="block font-medium text-sm mb-2">
                                    State <span className="text-red-500">*</span>
                                </label>
                                <Dropdown id="state" value={formData.state} options={STATES} optionLabel="label" optionValue="value"
                                    onChange={handleField('state')} placeholder="Select state" filter filterPlaceholder="Search states..."
                                    showClear disabled={loading} className={fieldClass('state')}
                                    aria-invalid={!!errors.state} aria-describedby="state-help" />
                                {errTip('state', 'state-help')}
                            </div>
                            <div className="field col-12 md:col-4">
                                <label htmlFor="pincode" className="block font-medium text-sm mb-2">
                                    Pincode <span className="text-red-500">*</span>
                                </label>
                                <IconField iconPosition="left" className="w-full">
                                    <InputIcon className="pi pi-hashtag" />
                                    <InputText id="pincode" value={formData.pincode} onChange={handleField('pincode', (v) => v.replace(/\D/g, '').slice(0, 6))}
                                        onBlur={handlePincodeBlur} placeholder="6-digit pincode" maxLength={6}
                                        inputMode="numeric" autoComplete="postal-code"
                                        disabled={loading} className={fieldClassMono('pincode')} aria-invalid={!!errors.pincode} aria-describedby="pincode-help" />
                                </IconField>
                                {errTip('pincode', 'pincode-help')}
                            </div>
                            <div className="field col-12 md:col-4">
                                <label htmlFor="address" className="block font-medium text-sm mb-2">
                                    Address <span className="text-red-500">*</span>
                                </label>
                                <InputTextarea id="address" value={formData.address} onChange={handleField('address')}
                                    placeholder="Enter complete address" maxLength={300} rows={1} autoResize
                                    disabled={loading} className={fieldClass('address')} aria-invalid={!!errors.address} aria-describedby="address-help" />
                                {errTip('address', 'address-help')}
                            </div>
                        </div>

                        <ConsentCheckbox
                            checked={formData.consent}
                            onChange={handleConsentChange}
                            error={errors.consent}
                            reportType="equifax"
                        />

                        <div className="flex justify-content-end mt-3">
                            <Button
                                type="submit"
                                label="Generate Credit Report"
                                icon="pi pi-arrow-right"
                                iconPos="right"
                                loading={loading}
                                disabled={loading}
                                size="small"
                            />
                        </div>
                    </form>
                ) : (
                    <>
                        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2 mb-3">
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-check-circle text-green-500 pi-2x" aria-hidden="true" />
                                <div>
                                    <p className="text-sm font-semibold text-900 m-0">Credit report generated</p>
                                    <p className="text-xs text-500 m-0">Report for {formData.pan} · {formData.full_name}</p>
                                </div>
                            </div>
                            <Button
                                label="Generate another"
                                icon="pi pi-refresh"
                                severity="secondary"
                                outlined
                                size="small"
                                onClick={handleAnother}
                            />
                        </div>
                        {/* Unprotected aggregate summary — CIBIL score + active/closed loan
                            counts + EMI. These are non-PII aggregates the DSA needs at a
                            glance without entering the PIN that gates the full report. */}
                        {(() => {
                            const extracted =
                                reportData?.data?.extracted_data
                                ?? reportData?.extracted_data
                                ?? null;
                            if (!extracted) return null;
                            const cibil = extracted.cibil_score;
                            const activeLoans = Array.isArray(extracted.existing_loans) ? extracted.existing_loans.length : 0;
                            // Closed loans = total account count - active (when both available).
                            // total_accounts is on profile_data.account_summary if backend forwards it;
                            // otherwise show "—".
                            const acctSummary = reportData?.data?.profile_data?.account_summary
                                ?? reportData?.profile_data?.account_summary
                                ?? null;
                            const totalAccounts = acctSummary?.total_accounts
                                ?? acctSummary?.number_of_accounts
                                ?? null;
                            const closedLoans = (totalAccounts != null) ? Math.max(0, totalAccounts - activeLoans) : null;
                            const emi = extracted.total_existing_emi || 0;

                            const cibilSeverity = cibil >= 750 ? 'success'
                                : cibil >= 700 ? 'info'
                                : cibil >= 600 ? 'warning'
                                : 'danger';

                            return (
                                <div className="surface-50 border-1 surface-border border-round p-3 mb-3">
                                    <div className="grid">
                                        <div className="col-6 md:col-3">
                                            <div className="text-xs text-500 mb-1">CIBIL Score</div>
                                            <div className={`text-xl font-semibold text-${cibilSeverity === 'success' ? 'green' : cibilSeverity === 'info' ? 'blue' : cibilSeverity === 'warning' ? 'orange' : 'red'}-600`}>
                                                {cibil ?? '—'}
                                            </div>
                                        </div>
                                        <div className="col-6 md:col-3">
                                            <div className="text-xs text-500 mb-1">Active Loans</div>
                                            <div className="text-xl font-semibold text-900">{activeLoans}</div>
                                        </div>
                                        <div className="col-6 md:col-3">
                                            <div className="text-xs text-500 mb-1">Closed Loans</div>
                                            <div className="text-xl font-semibold text-900">{closedLoans ?? '—'}</div>
                                        </div>
                                        <div className="col-6 md:col-3">
                                            <div className="text-xs text-500 mb-1">Total EMI</div>
                                            <div className="text-xl font-semibold text-900">
                                                {emi > 0 ? `₹${parseFloat(emi).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                        <EquifaxOutputComponent
                            reportData={reportData}
                            onDownloadPDF={handleDownloadPDF}
                            pdfDownloadUrl={reportData?.pdf_download_url || reportData?.data?.pdf_download_url}
                            serviceDetails={serviceDetails}
                            actualServiceDetails={serviceDetails}
                        />
                    </>
                )}
            </Card>
        </>
    );
};

export default EquifaxInputComponent;
